window.addEventListener('DOMContentLoaded', () => {
  /** @type {AbortController | null} */
  let pendingController = null;

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  const getSectionElement = (card) => card.closest('.shopify-section');

  /**
   * @param {HTMLElement | null} sectionElement
   * @returns {string | null}
   */
  const getSectionId = (sectionElement) => {
    if (!sectionElement || !sectionElement.id) return null;
    return sectionElement.id.replace('shopify-section-', '');
  };

  /**
   * @param {string} variantUrl
   * @param {string} sectionId
   * @returns {string}
   */
  const buildSectionUrl = (variantUrl, sectionId) => {
    const url = new URL(variantUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  /**
   * @returns {Promise<void>}
   */
  const waitForAnimationFrame = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });

  /**
   * 返回当前配置下会遮挡视口顶部的 sticky header 高度。
   * scroll-up 模式下位置校正本身会触发向上滚动，因此也需要提前预留 header 高度。
   * @returns {number}
   */
  const getStickyHeaderOffset = () => {
    const header = document.querySelector('#header-group #header-component[sticky]');
    if (!(header instanceof HTMLElement)) return 0;

    const cssHeaderHeight = Number.parseFloat(
      getComputedStyle(document.body).getPropertyValue('--header-height')
    );

    return Number.isFinite(cssHeaderHeight) && cssHeaderHeight > 0
      ? cssHeaderHeight
      : header.offsetHeight;
  };

  /**
   * Section 更新会收起旧卡片并改变新激活卡片的位置；布局稳定后确保其顶部仍在可视区域内。
   * @param {HTMLElement} sectionElement
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  const keepActiveCardTopVisible = async (sectionElement, signal) => {
    await waitForAnimationFrame();
    await waitForAnimationFrame();

    if (signal.aborted || !sectionElement.isConnected) return;

    const activeCard = sectionElement.querySelector(
      '.spx-variant-picker .spx-related-product-card.current-product'
    );
    if (!(activeCard instanceof HTMLElement)) return;

    const viewportTop = getStickyHeaderOffset();
    const cardTop = activeCard.getBoundingClientRect().top;
    if (cardTop >= viewportTop) return;

    window.scrollBy({
      top: cardTop - viewportTop,
      left: 0,
      behavior: 'auto',
    });
  };

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('.spx-variant-picker .spx-related-product-card');
    if (!(card instanceof HTMLElement)) return;

    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();

    const variantUrl = card.dataset.variantUrl;
    if (!variantUrl) return;

    const sectionElement = getSectionElement(card);
    const sectionId = getSectionId(sectionElement);
    if (!sectionElement || !sectionId) return;

    pendingController?.abort();
    const requestController = new AbortController();
    pendingController = requestController;

    try {
      const response = await fetch(buildSectionUrl(variantUrl, sectionId), {
        signal: requestController.signal,
        headers: { Accept: 'text/html' },
      });
      const htmlText = await response.text();

      const parsedDoc = new DOMParser().parseFromString(htmlText, 'text/html');
      const nextSection = parsedDoc.querySelector(`#shopify-section-${sectionId}`);
      if (!nextSection || requestController.signal.aborted) return;

      sectionElement.innerHTML = nextSection.innerHTML;
      sectionElement.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId },
        })
      );
      history.replaceState({}, '', variantUrl);
      await keepActiveCardTopVisible(sectionElement, requestController.signal);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      // 网络异常时退回到原始跳转，避免点击无响应
      window.location.href = variantUrl;
    } finally {
      if (pendingController === requestController) {
        pendingController = null;
      }
    }
  });
});
